import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Flex } from "@twilio-paste/core/flex";
import { ScreenReaderOnly } from "@twilio-paste/core/screen-reader-only";
import { Text } from "@twilio-paste/core/text";
import { CallIcon } from "@twilio-paste/icons/esm/CallIcon";
import { LinkExternalIcon } from "@twilio-paste/icons/esm/LinkExternalIcon";
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";
import { Media, Message } from "@twilio/conversations";
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { setShowMessageInput } from "../store/actions/genericActions";
import { AppState } from "../store/definitions";
import { CallToActionOption } from "../types/bots";
import { extractUrls, parseMessageBody } from "../utils/parseMessageBody";
import { FilePreview } from "./FilePreview";
import * as styles from "./styles/MessageBubble.styles";

import useLinkPreview from "use-link-preview";
import { capitalize } from "../utils/capitalize";
import { isUUID } from "../utils/isUUID";
import { InlineToggleButton } from "./LinkPreview/InlineToggleButton";
import { LinkPreview } from "./LinkPreview/LinkPreview";
import { MessageActions } from "./MessageActions";

const doubleDigit = (number: number) => `${number < 10 ? "0" : ""}${number}`;

const messageBubbleSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        conversationsClient: state.chat.conversationsClient,
        participants: state.chat.participants,
        users: state.chat.users,
        fileAttachmentConfig: state.config.attachment,
        botName: state.config.bot.botName || "bot"
    })
);

interface MessageBubbleProps {
    message: Message;
    isLast: boolean;
    isLastOfUserGroup: boolean;
    focusable: boolean;
    updateFocus: (newFocus: number) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    isLast,
    isLastOfUserGroup,
    focusable,
    updateFocus
}) => {
    const [read, setRead] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [isPreviewVisible, setIsPreviewVisible] = useState(true);
    const { conversationsClient, participants, users, fileAttachmentConfig, botName } = useSelector(messageBubbleSelector);
    const messageRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const messageAttributes = message?.attributes as Record<string, unknown> | undefined;
    const header = messageAttributes?.header as string | undefined;
    const footer = messageAttributes?.footer as string | undefined;
    const cta = messageAttributes?.cta as CallToActionOption[] | undefined;

    const isBot = (message.attributes as { sender: "bot" | "user" })?.sender === "bot";
    let author = isBot ? botName
        : (
            users?.find((u) => u.identity === message.author)?.friendlyName?.split(" ")[0]
            || capitalize(decodeURI((message.author ?? "User").replace("_", "%"))?.split(".")[0]) //message.author is URI encoded but with _s instead of %s
        );

    if (isUUID(author)) {
        author = 'You';
    }
    const belongsToCurrentUser = !isBot && message.author === conversationsClient?.user.identity;

    // If a quick response is the latest message, hide the message input.
    useEffect(() => {
        if (isLast && !belongsToCurrentUser && message.media?.contentType.includes("quick-reply")) {
            dispatch(setShowMessageInput(false));
        }
    }, [isLast, belongsToCurrentUser, message, dispatch]);

    useEffect(() => {
        if (isLast && participants && belongsToCurrentUser) {
            const otherParticipants = participants.filter((p) => p.identity !== conversationsClient?.user.identity);
            setRead(
                Boolean(otherParticipants.length) &&
                otherParticipants.every((p) => p.lastReadMessageIndex === message.index)
            );
        } else {
            setRead(false);
        }
    }, [participants, isLast, belongsToCurrentUser, conversationsClient, message]);

    useEffect(() => {
        if (focusable) {
            messageRef.current?.focus();
        }
    }, [focusable]);

    const renderMedia = () => {
        if (!fileAttachmentConfig?.enabled || !message.attachedMedia) {
            return <i>Media messages are not supported</i>;
        }

        return message.attachedMedia.map((media: Media, index: number) => (
            <FilePreview
                key={index}
                file={{ name: media.filename, type: media.contentType, size: media.size } as File}
                isBubble={true}
                media={media}
                focusable={focusable}
            />
        ));
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            updateFocus(message.index + (e.key === "ArrowUp" ? -1 : 1));
        }
    };

    const handleFocus = () => {
        if (!isMouseDown) {
            updateFocus(message.index);
        }
    };

    const renderCallToAction = (cta: CallToActionOption) => {
        switch (cta.type) {
            case "PHONE_NUMBER":
                return (
                    <Button variant="secondary" as="a" key={cta.title} href={`tel:${cta.phone}`}>
                        <CallIcon decorative /> {cta.phone}
                    </Button>
                );
            case "URL":
                return (
                    <Button variant="secondary" as="a" key={cta.title} href={cta.url}>
                        <LinkExternalIcon decorative /> {cta.title}
                    </Button>
                );
            default:
                return null;
        }
    };
    if ((message?.attributes as { isKickOffMessage?: boolean })?.isKickOffMessage) {
        return null;
    }

    const urls = message.body ? extractUrls(message.body) : [];
    const previews = urls.map(url => useLinkPreview(url) || "");

    return (
        <Box
            {...styles.outerContainerStyles}
            tabIndex={focusable ? 0 : -1}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onMouseDown={() => setIsMouseDown(true)}
            onMouseUp={() => setIsMouseDown(false)}
            ref={messageRef}
            data-message-bubble
            data-testid="message-bubble"
        >
            <Box display="flex" flexDirection="column">
                <Box {...styles.bubbleAndAvatarContainerStyles}>
                    {!belongsToCurrentUser && (
                        <Box {...styles.getAvatarContainerStyles(!isLastOfUserGroup)} data-testid="avatar-container">
                            {isLastOfUserGroup && <UserIcon decorative={true} size="sizeIcon40" />}
                        </Box>
                    )}
                    <Box {...styles.getInnerContainerStyles(belongsToCurrentUser)}>
                        <Flex hAlignContent="between" width="100%" vAlignContent="center" marginBottom="space20">
                            <Text
                                {...styles.authorStyles}
                                as="p"
                                aria-hidden
                                style={{ textOverflow: "ellipsis" }}
                                title={author ?? undefined}
                            >
                                {author}
                            </Text>
                            <ScreenReaderOnly as="p">
                                {belongsToCurrentUser ? "You sent at" : `${author} sent at`}
                            </ScreenReaderOnly>
                            {message.dateCreated && (
                                <Text {...styles.timeStampStyles} as="p">
                                    {`${doubleDigit(message.dateCreated.getHours())}:${doubleDigit(message.dateCreated.getMinutes())}`}
                                </Text>
                            )}
                        </Flex>
                        {header && (
                            <Text as="h3" {...styles.headerStyles}>
                                {header}
                            </Text>
                        )}
                        <Text as="p" {...styles.bodyStyles}>
                            {/* This has been sanitised. */}
                            <span dangerouslySetInnerHTML={{
                                __html: message.body ? parseMessageBody(message.body) : ''
                            }}
                            />
                            {urls.length > 0 && (
                                <InlineToggleButton
                                    isPreviewVisible={isPreviewVisible}
                                    onClick={() => setIsPreviewVisible(!isPreviewVisible)}
                                />
                            )}
                            {cta && (
                                <Box as="span" {...styles.getCtaStyles(belongsToCurrentUser)}>
                                    {cta.map(renderCallToAction)}
                                </Box>
                            )}
                        </Text>
                        {(!belongsToCurrentUser && message.media?.contentType.includes("quick-reply")) && <MessageActions message={message} isLast={isLast} />}
                        {urls.length > 0 && isPreviewVisible && (
                            <Box marginTop="space20">
                                {urls.map((url, index) => {
                                    const { metadata, isLoading } = previews[index];
                                    return metadata && !isLoading ? (
                                        <LinkPreview
                                            key={url}
                                            metadata={metadata}
                                        />
                                    ) : null;
                                })}
                            </Box>
                        )}
                        {footer && (
                            <Text as="h3" {...styles.footerStyles}>
                                {footer}
                            </Text>
                        )}
                        {message.type === "media" && renderMedia()}
                    </Box>
                </Box>
            </Box>
            {read && (
                <Flex hAlignContent="right" vAlignContent="center" marginTop="space20">
                    <Text as="p" {...styles.readStatusStyles}>
                        Read
                    </Text>
                    <SuccessIcon decorative={true} size="sizeIcon10" color="colorTextWeak" />
                </Flex>
            )}
        </Box>
    );
};
